import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import type { Task } from "@/types/task";
import { TaskList } from "@/components/tasks/TaskList";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { Button } from "@/components/ui/button";
import { CheckSquare2, Plus, RefreshCw } from "lucide-react";

interface DealTasksCardProps {
  dealId: string;
  dealName: string;
  isClosed?: boolean;
}

export function DealTasksCard({
  dealId,
  dealName,
  isClosed = false,
}: DealTasksCardProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/api/Task/List?related_type=DEAL&related_id=${dealId}&scope=all&page_size=100`
      );
      const data = unwrapPage<Task>(res).rows;
      setTasks(data);
    } catch (err) {
      console.warn("Could not load tasks for deal:", err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const openCount = tasks.filter((t) => t.task_status === "OPEN").length;

  return (
    <div className="bg-card border border-border rounded-2xl shadow-xs overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
            <CheckSquare2 className="size-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold tracking-tight text-foreground">
                Tasks & Reminders
              </h3>
              {openCount > 0 && (
                <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  {openCount} active
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Scheduled follow-ups and action items for this deal
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={fetchTasks}
            title="Refresh tasks"
            className="size-8 text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="size-3.5" />
          </Button>

          {!isClosed && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCreateDialogOpen(true)}
              className="h-8 gap-1.5 text-xs font-medium"
            >
              <Plus className="size-3.5" />
              <span>Add Task</span>
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-3.5 sm:p-4">
        <TaskList
          tasks={tasks}
          loading={loading}
          onRefresh={fetchTasks}
          showAssignee={true}
          hideRelated={true}
          emptyMessage="No tasks scheduled for this deal yet."
        />
      </div>

      {/* Add Task Dialog */}
      <TaskDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        defaultRelated={{
          type: "DEAL",
          id: dealId,
          name: dealName,
        }}
        onSaved={fetchTasks}
      />
    </div>
  );
}
